import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { Screen, Section, Facts, Rows, Row, Badge, EmptyState, ago } from '../ui/kit.js';
import { meta } from '../phases.js';
import { reporter } from '../pwa/report.js';

interface StatsData {
  jobs: { id: string; title: string; mode: string; phase: string; repo?: string; account?: string | null; cost_usd: number | null; ci_cycles: number; created_at: string; minutes: number }[];
  ci: {
    recent: { run_id: number; head_sha: string; conclusion: string | null; duration_s: number | null }[];
    passRate: number | null;
    superseded: number;
    p50DurationS: number | null;
  };
  spend30d: { usd: number | null };
}

// The red-loop problem, visible and provable over time: pass rate on main,
// time to green, superseded runs — beside every job's own outcome.
export function History() {
  const navigate = useNavigate();
  const [data, setData] = useState<StatsData | null>(null);

  useEffect(() => {
    void api.get<StatsData>('/api/stats').then(setData).catch(reporter('the history'));
  }, []);

  if (!data) {
    return (
      <Screen title="History">
        <EmptyState title="Crunching…" />
      </Screen>
    );
  }

  const accountNames = new Set(data.jobs.map((j) => j.account).filter(Boolean));
  const manyAccounts = accountNames.size > 1;

  return (
    <Screen title="History" sub="Jobs, outcomes, and how CI is behaving on main.">
      <Facts
        items={[
          {
            label: 'Main pass rate',
            value: data.ci.passRate === null ? '—' : `${Math.round(data.ci.passRate * 100)}%`,
          },
          {
            label: 'p50 time to green',
            value: data.ci.p50DurationS ? `${Math.round(data.ci.p50DurationS / 60)} min` : '—',
          },
          { label: 'Superseded runs', value: data.ci.superseded },
          { label: '30-day spend', value: data.spend30d?.usd ? `$${data.spend30d.usd}` : '$0' },
        ]}
      />

      <Section title="Jobs">
        {data.jobs.length === 0 ? (
          <EmptyState title="No finished jobs yet." />
        ) : (
          <Rows>
            {data.jobs.map((j) => {
              const m = meta(j.phase);
              return (
                <Row
                  key={j.id}
                  title={j.title}
                  sub={`${j.repo && j.repo !== 'meridian' ? 'DHS demo · ' : ''}${j.mode} · ${j.minutes} min · ${j.ci_cycles} red cycle${j.ci_cycles === 1 ? '' : 's'}${j.cost_usd ? ` · $${j.cost_usd.toFixed(2)}` : ''}${manyAccounts && j.account ? ` · ${j.account}` : ''} · ${ago(j.created_at)} ago`}
                  end={<Badge tone={m.tone}>{m.label}</Badge>}
                  onTap={() => navigate(`/jobs/${j.id}`)}
                />
              );
            })}
          </Rows>
        )}
      </Section>

      <Section title="Recent main runs">
        <div className="ops-card">
          {data.ci.recent.length === 0 ? (
            <p className="ops-hint" style={{ margin: 0 }}>
              Main-run history fills in as pushes happen.
            </p>
          ) : (
            data.ci.recent.map((r) => (
              <div key={r.run_id} className="ops-ci__row">
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background:
                      r.conclusion === 'success'
                        ? 'var(--ops-ok)'
                        : r.conclusion === 'cancelled'
                          ? 'var(--ops-wait)'
                          : r.conclusion
                            ? 'var(--ops-bad)'
                            : 'var(--ops-run)',
                  }}
                />
                <span className="ops-ci__name">{r.head_sha.slice(0, 8)}</span>
                <span>{r.duration_s ? `${Math.round(r.duration_s / 60)}m` : ''}</span>
                <span>{r.conclusion ?? 'running'}</span>
              </div>
            ))
          )}
        </div>
      </Section>
    </Screen>
  );
}
